import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { GridColDef } from '@mui/x-data-grid-premium';
import { Table } from '../../../../components/Table';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { fetchUserUsage } from '../../../../redux/modules/user/user.actions';
import { userUsageSelector } from '../../../../redux/modules/user/user.selectors';
import { formatDateTime } from '../../../../utils/date';
import { TableCard } from '../../../../components/Section';

interface SubscriptionTabProps {
  id?: number;
}

function formatSubscriptionDate(value: string | number | Date | null | undefined): string {
  if (value == null) return '—';
  const date = typeof value === 'number' ? new Date(value) : new Date(value as string);
  return Number.isNaN(date.getTime()) ? '—' : formatDateTime(date.toISOString());
}

function formatStatus(status: string): string {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

function SubscriptionTab({ id }: SubscriptionTabProps) {
  const columns = useRef<GridColDef[]>([
    {
      field: 'status',
      headerName: 'Premium State',
      width: 140,
      valueFormatter: (params) => formatStatus((params.value as string) ?? ''),
    },
    {
      field: 'name',
      headerName: 'Plan',
      width: 200,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 180,
      type: 'dateTime',
      valueFormatter: (params) => formatSubscriptionDate(params.value as string | number | Date),
    },
    {
      field: 'expiresAt',
      headerName: 'End Date',
      width: 180,
      type: 'dateTime',
      valueFormatter: (params) => formatSubscriptionDate(params.value as string | null),
    },
  ]);

  const { totalUserUsageItems, userUsageItems, fetchUserUsageStatus } = useSelector(userUsageSelector);

  if (id == null) {
    return <div>No user selected.</div>;
  }

  return (
    <TableCard title="Subscription" description="Premium plan history for this user">
      <Table
        fetchItems={fetchUserUsage}
        fetchItemsCustomParams={{ userId: id }}
        rows={userUsageItems}
        columns={columns.current}
        loading={fetchUserUsageStatus === fetchStatuses.pending}
        rowCount={totalUserUsageItems}
        disableColumnFilter
        disableColumnMenu
        density="compact"
        initialSortModel={[{ field: 'expiresAt', sort: 'desc' }]}
        wrapperHeight={300}
        toolbarExportProps={{
          csvOptions: { fileName: `user-${id}-usage-${Date.now()}` },
          excelOptions: { fileName: `user-${id}-usage-${Date.now()}` },
        }}
      />
    </TableCard>
  );
}

export default SubscriptionTab;
